import { lazy, ReactElement } from "react";
import { Navigate } from "react-router-dom";

// project imports
import MainLayout from "features/layout/MainLayout";
import IncognitoGuard from "features/layout/IncognitoGuard";
import AuthGuard from "features/layout/AuthGuard";
import Loadable from "routes/Loadable";

// Page routing
const NotFoundPage = Loadable(lazy(() => import("features/pages/404")));
const ProgressPage = Loadable(lazy(() => import("features/progress")));
const TugPage = Loadable(lazy(() => import("features/tug")));
const HistoryPage = Loadable(lazy(() => import("features/history")));
const AlbumsPage = Loadable(lazy(() => import("features/photos/albums")));
const PrivacyPage = Loadable(lazy(() => import("features/pages/copy/privacy")));
const AboutPage = Loadable(lazy(() => import("features/pages/about")));
const ProfilePage = Loadable(lazy(() => import("features/profile")));
const MembershipPage = Loadable(lazy(() => import("features/membership")));
const CommunityPage = Loadable(lazy(() => import("features/members")));
const KudosPage = Loadable(lazy(() => import("features/pages/kudos")));
const DeletePage = Loadable(lazy(() => import("features/pages/copy/delete")));
const TermsPage = Loadable(lazy(() => import("features/pages/copy/terms")));
const ChatsPage = Loadable(lazy(() => import("features/chat")));
const LiveFeedPage = Loadable(lazy(() => import("features/feed")));
const DirectoryPage = Loadable(lazy(() => import("features/pages/Directory")));
const MapPage = Loadable(lazy(() => import("features/map")));
const LoginPage = Loadable(lazy(() => import("features/pages/login")));
const BugsPage = Loadable(lazy(() => import("features/bugs/bugs")));

// Prefetch main tab chunks after initial load so tab switching is instant
if (typeof window !== "undefined") {
  const prefetch = () => {
    import("features/tug");
    import("features/history");
    import("features/progress");
    import("features/feed");
    import("features/pages/Directory");
  };
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(prefetch);
  } else {
    setTimeout(prefetch, 2000);
  }
}

interface RouteConfig {
  path: string;
  element: ReactElement;
}

interface MainRoutesConfig {
  path: string;
  element: ReactElement;
  children: RouteConfig[];
}

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: MainRoutesConfig = {
  path: "/",
  element: <MainLayout />,
  children: [
    {
      // Land directly on the Tug page. MainLayout's AuthGuard redirects signed-out
      // visitors to /login (no flash: it renders nothing until auth resolves).
      path: "/",
      element: <TugPage />,
    },
    {
      path: "login",
      element: <LoginPage />,
    },
    {
      path: "tug",
      element: <TugPage />,
    },
    {
      path: "progress",
      element: <ProgressPage />,
    },
    {
      path: "history",
      element: <HistoryPage />,
    },
    {
      path: "bugs",
      element: <BugsPage />,
    },
    {
      path: "albums",
      element: <AlbumsPage />,
    },
    {
      path: "photos",
      element: <AlbumsPage />,
    },
    {
      path: "about",
      element: <AboutPage />,
    },
    {
      path: "profile",
      element: <ProfilePage />,
    },
    {
      path: "support",
      element: <MembershipPage />,
    },
    {
      path: "membership",
      element: <MembershipPage />,
    },
    {
      path: "community",
      element: (
        <AuthGuard>
          <IncognitoGuard>
            <CommunityPage />
          </IncognitoGuard>
        </AuthGuard>
      ),
    },
    {
      path: "kudos",
      element: (
        <AuthGuard>
          <IncognitoGuard>
            <KudosPage />
          </IncognitoGuard>
        </AuthGuard>
      ),
    },
    {
      path: "search",
      element: (
        <AuthGuard>
          <IncognitoGuard>
            <CommunityPage />
          </IncognitoGuard>
        </AuthGuard>
      ),
    },
    {
      path: "data",
      element: <Navigate to="/profile?tab=data" replace />,
    },
    {
      path: "delete",
      element: <DeletePage />,
    },
    {
      path: "privacy",
      element: <PrivacyPage />,
    },
    {
      path: "terms",
      element: <TermsPage />,
    },
    {
      path: "chats",
      element: (
        <AuthGuard>
          <IncognitoGuard>
            <ChatsPage />
          </IncognitoGuard>
        </AuthGuard>
      ),
    },
    {
      path: "feed",
      element: (
        <IncognitoGuard>
          <LiveFeedPage />
        </IncognitoGuard>
      ),
    },
    {
      path: "directory",
      element: <DirectoryPage />,
    },
    {
      path: "map",
      element: (
        <AuthGuard>
          <IncognitoGuard>
            <MapPage />
          </IncognitoGuard>
        </AuthGuard>
      ),
    },
    {
      path: "*",
      element: <NotFoundPage />,
    },
  ],
};

export default MainRoutes;
